
import { useState } from "react";
import { Bird, Fish, Cat, Dog, Rabbit, Footprints, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const animals = [
  {
    name: "Cat",
    icon: <Cat size={40} />,
    color: "bg-gradient-to-br from-tinyminds-coral/30 to-white",
    facts: [
      "Cats sleep for about 16 hours a day!",
      "Cats have 32 muscles in each ear.",
      "A group of cats is called a clowder."
    ]
  },
  {
    name: "Dog",
    icon: <Dog size={40} />,
    color: "bg-gradient-to-br from-tinyminds-yellow/30 to-white",
    facts: [
      "Dogs have a sense of smell that's 40 times better than humans.",
      "A dog's nose print is unique, like a human's fingerprint.",
      "Dogs can understand up to 250 words and gestures."
    ]
  },
  {
    name: "Bird",
    icon: <Bird size={40} />,
    color: "bg-gradient-to-br from-tinyminds-skyblue/30 to-white",
    facts: [
      "Birds have hollow bones to help them fly.",
      "Some birds can sleep with one eye open.",
      "There are more than 10,000 different species of birds."
    ]
  },
  {
    name: "Fish",
    icon: <Fish size={40} />,
    color: "bg-gradient-to-br from-tinyminds-purple/30 to-white",
    facts: [
      "Some fish can change their color to hide from predators.",
      "Most fish don't have eyelids, so they sleep with their eyes open.",
      "Fish use their gills to breathe underwater."
    ]
  },
  {
    name: "Rabbit",
    icon: <Rabbit size={40} />,
    color: "bg-gradient-to-br from-tinyminds-yellow/30 to-white",
    facts: [
      "Rabbits have nearly 360-degree vision.",
      "Baby rabbits are called kittens or kits.",
      "Rabbits can hop up to 3 feet high!"
    ]
  },
  {
    name: "Owl",
    icon: <Sparkles size={40} />,
    color: "bg-gradient-to-br from-tinyminds-coral/30 to-white",
    image: "/lovable-uploads/8e63f009-6b00-4d96-acb5-1ff76c4e80a8.png",
    facts: [
      "Owls can turn their heads almost 270 degrees!",
      "Owls have special feathers that make their flight silent.",
      "An owl's eyes are so big that they can't move them - they have to turn their whole head!"
    ]
  },
  {
    name: "Kangaroo",
    icon: <Footprints size={40} />,
    color: "bg-gradient-to-br from-tinyminds-skyblue/30 to-white",
    image: "/lovable-uploads/23dc5b6d-b817-4851-88f5-648281eda9fe.png",
    facts: [
      "Kangaroos can jump up to 30 feet in one hop!",
      "Baby kangaroos are called joeys and are the size of a bean when born.",
      "A group of kangaroos is called a mob."
    ]
  },
  {
    name: "Hippo",
    icon: <Footprints size={40} />,
    color: "bg-gradient-to-br from-tinyminds-purple/30 to-white",
    image: "/lovable-uploads/03c0ae76-0678-43fc-873b-3a265bf5d108.png",
    facts: [
      "Hippos can hold their breath underwater for up to 5 minutes!",
      "Despite weighing up to 4 tons, hippos can run faster than humans.",
      "Hippos produce their own natural sunscreen - a red substance from their skin."
    ]
  }
];

const AnimalFacts = () => {
  const [selectedAnimal, setSelectedAnimal] = useState(animals[0]);
  const [currentFact, setCurrentFact] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleAnimalClick = (animal) => {
    setSelectedAnimal(animal);
    setCurrentFact(0);
  };

  const nextFact = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentFact((prev) => (prev + 1) % selectedAnimal.facts.length);
      setIsAnimating(false);
    }, 300);
  };

  return (
    <section className="py-16 bg-gradient-kiddy">
      <div className="container mx-auto px-4">
        <h2 className="section-title text-center flex justify-center items-center mb-8 animate-bounce text-white">
          <Footprints className="mr-2 text-tinyminds-yellow" />
          Animal Facts for Little Explorers
        </h2>
        
        <div className="flex flex-wrap justify-center gap-4 mt-6 mb-8">
          {animals.map((animal) => (
            <Button
              key={animal.name}
              variant={selectedAnimal.name === animal.name ? "default" : "outline"}
              className={`flex items-center gap-2 transition-all duration-300 transform hover:scale-110 rounded-full ${
                selectedAnimal.name === animal.name 
                  ? "bg-tinyminds-coral text-white" 
                  : "bg-white hover:bg-tinyminds-yellow hover:text-tinyminds-text border-tinyminds-skyblue"
              }`}
              onClick={() => handleAnimalClick(animal)}
            >
              <span className={`${selectedAnimal.name === animal.name ? "animate-jiggle" : "animate-pulse"}`}>{animal.icon}</span>
              <span className="font-baloo">{animal.name}</span>
            </Button>
          ))}
        </div>
        
        <Card className={`max-w-2xl mx-auto shadow-lg ${selectedAnimal.color} border-none rounded-3xl transform transition-all duration-500 hover:shadow-xl animate-pop-in`}>
          <CardContent className="p-8">
            <div className="flex items-center justify-center mb-6">
              {selectedAnimal.image ? (
                <div className="w-28 h-28 rounded-full overflow-hidden shadow-md">
                  <img 
                    src={selectedAnimal.image} 
                    alt={selectedAnimal.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="p-4 rounded-full bg-white text-tinyminds-skyblue shadow-md animate-float">
                  {selectedAnimal.icon}
                </div>
              )}
              <h3 className="text-2xl font-baloo ml-4 bg-gradient-to-r from-tinyminds-coral to-tinyminds-skyblue bg-clip-text text-transparent">{selectedAnimal.name}</h3>
            </div>
            
            <div className="min-h-[100px] flex items-center justify-center">
              <p className={`text-xl text-center font-comic-neue ${isAnimating ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0 animate-slide-in'} transition-all duration-300`}>
                {selectedAnimal.facts[currentFact]}
              </p>
            </div>
            
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                {selectedAnimal.facts.map((_, index) => (
                  <span
                    key={index}
                    className={`block w-3 h-3 rounded-full transition-all duration-300 ${
                      currentFact === index 
                        ? 'bg-tinyminds-coral scale-125' 
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex justify-center mt-6">
              <Button 
                onClick={nextFact} 
                className="bg-gradient-kiddy text-white hover:bg-gradient-fun transition-all duration-300 transform hover:scale-105 shadow-md rounded-full"
              >
                Next Fact
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default AnimalFacts;
